import type {
  NamedRef,
  PrivateEventHistoryChange,
  PrivateEventHistoryEntry,
} from '../../api/private-event-history';
import { PRIVATE_EVENT_ADD_ONS } from '../../api/privateEvents';
import { formatVenueDate, formatVenueTime } from '../../utils/venueTime';
import {
  processAuditEntry,
  type AuditSubRow,
} from '../dashboards/workshops/detail/auditEventHelpers';

import { friendlyZoneName } from './friendlyTimeZone';
import { locationRows } from './history-location-changes';

export type HistoryChangeView =
  | { kind: 'headcount'; key: string; before: number; after: number; delta: number }
  | { kind: 'text'; key: string; label: string; before: string; after: string }
  | { kind: 'crew'; key: string; subRows: AuditSubRow[] };

export interface HistoryEntryView {
  id: string;
  /** The creation and import rows, which close the list. */
  marker: boolean;
  /** Marker rows, and any entry with no readable change to speak for it. */
  showTitle: boolean;
  title: string;
  actorName: string;
  changedAt: string;
  changes: HistoryChangeView[];
}

const NONE = '—';

const ENTRY_TITLES: Record<string, string> = {
  created: 'Created',
  imported: 'Imported from FareHarbor',
  import_updated: 'Updated from FareHarbor',
  cancelled: 'Cancelled',
  edited: 'Edited',
};
const MARKER_TYPES = new Set(['created', 'imported', 'import_updated']);

const isRef = (v: unknown): v is NamedRef =>
  typeof v === 'object' && v !== null && 'name' in v && 'id' in v;

const text = (v: unknown) => (v === null || v === undefined || v === '' ? NONE : String(v));
const person = (v: unknown) => (isRef(v) ? v.name : NONE);
const humanize = (field: string) => {
  const spaced = field.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};
const dollars = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
const lifecycle = (v: unknown) => {
  const word = text(v);
  return word === NONE ? word : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};
const addOnLabel = (id: string) => PRIVATE_EVENT_ADD_ONS.find((a) => a.id === id)?.label ?? id;

/** A wall-clock-encoded string: read its clock face as written, never shifted
 *  to the viewer's zone. */
const wallClock = (v: unknown) =>
  typeof v === 'string' ? `${formatVenueDate(v, 'MMM d')}, ${formatVenueTime(v)}` : NONE;

interface FieldFormat {
  label: string;
  format: (value: unknown) => string;
}

const FIELDS: Record<string, FieldFormat> = {
  startsAt: { label: 'Start', format: wallClock },
  endsAt: { label: 'End', format: wallClock },
  workshopTypeId: { label: 'Workshop type', format: person },
  clientName: { label: 'Client', format: text },
  contactName: { label: 'Contact', format: text },
  contactEmail: { label: 'Contact email', format: text },
  contactPhone: { label: 'Contact phone', format: text },
  hubspotDealId: { label: 'HubSpot deal', format: text },
  hubspotPaymentId: { label: 'HubSpot payment', format: text },
  notes: { label: 'Notes', format: text },
  lifecycle: { label: 'Status', format: lifecycle },
  depositReceived: { label: 'Deposit received', format: (v) => (v ? 'Yes' : 'No') },
  dealValueCents: {
    label: 'Deal value',
    format: (v) => (typeof v === 'number' ? `${dollars(v)} est.` : NONE),
  },
  ownerUserId: { label: 'Regional coordinator', format: person },
  nationalCoordinatorUserId: { label: 'National coordinator', format: person },
  dealOriginatorUserId: { label: 'Deal originator', format: person },
  timeZone: {
    label: 'Time zone',
    format: (v) => (typeof v === 'string' ? friendlyZoneName(v) : NONE),
  },
  addOns: {
    label: 'Add-ons',
    format: (v) =>
      Array.isArray(v) && v.length > 0 ? v.map((id) => addOnLabel(String(id))).join(', ') : 'None',
  },
};

function crewView(
  entry: PrivateEventHistoryEntry,
  change: PrivateEventHistoryChange,
): HistoryChangeView {
  const shared = processAuditEntry({
    id: entry.id,
    eventType: 'edited',
    actorUserId: '',
    actorEmail: null,
    actorName: entry.actorName,
    changedAt: entry.changedAt,
    fieldChanges: { crew: { before: change.before, after: change.after } },
  });
  return { kind: 'crew', key: `${entry.id}-crew`, subRows: shared.subRows };
}

function changeViews(
  entry: PrivateEventHistoryEntry,
  change: PrivateEventHistoryChange,
): HistoryChangeView[] {
  if (change.field !== 'location') return [changeView(entry, change)];
  return locationRows(change.before, change.after).map((row) => ({
    kind: 'text',
    key: `${entry.id}-location-${row.label}`,
    ...row,
  }));
}

function changeView(
  entry: PrivateEventHistoryEntry,
  change: PrivateEventHistoryChange,
): HistoryChangeView {
  const key = `${entry.id}-${change.field}`;
  const { before, after } = change;
  if (change.field === 'crew') return crewView(entry, change);
  if (change.field === 'headcount' && typeof before === 'number' && typeof after === 'number') {
    return { kind: 'headcount', key, before, after, delta: after - before };
  }
  const fmt = FIELDS[change.field] ?? { label: humanize(change.field), format: text };
  return {
    kind: 'text',
    key,
    label: fmt.label,
    before: fmt.format(before),
    after: fmt.format(after),
  };
}

export function toHistoryEntryView(entry: PrivateEventHistoryEntry): HistoryEntryView {
  return {
    id: entry.id,
    marker: MARKER_TYPES.has(entry.eventType),
    showTitle: MARKER_TYPES.has(entry.eventType) || entry.changes.length === 0,
    title: ENTRY_TITLES[entry.eventType] ?? 'Edited',
    actorName: entry.actorName,
    changedAt: entry.changedAt,
    changes: entry.changes.flatMap((c) => changeViews(entry, c)),
  };
}
